import React, { useRef, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/standard-button';
import { token } from '@atlaskit/tokens';
import Spinner from '@atlaskit/spinner/spinner';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import type { AIImageAcceptanceRepositionerProps } from '../pageCoverPictureTypes.ts';
import { DEFAULT_POSITION_VALUE } from '../pageCoverPictureConstants.ts';

const i18n = defineMessages({
	dragToReposition: {
		id: 'page-cover-picture.ai-acceptance.drag.to.reposition.message',
		description:
			'Small overlay message that appears on top of AI generated cover picture when image repositioning is active.',
		defaultMessage: 'Drag to reposition',
	},
	accept: {
		id: 'page-cover-picture.ai-acceptance.accept',
		description: 'Button to accept the AI generated header image with the current position.',
		defaultMessage: 'Accept',
	},
	discard: {
		id: 'page-cover-picture.ai-acceptance.discard',
		description: 'Button to discard the AI generated header image.',
		defaultMessage: 'Discard',
	},
	retry: {
		id: 'page-cover-picture.ai-acceptance.retry',
		description: 'Button to regenerate a new AI header image.',
		defaultMessage: 'Retry',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RepositionWrapper = styled.div<{
	userOffline?: boolean;
}>({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	background: token('color.background.neutral'),
	visibility: 'visible',
	height: '40px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: ({ userOffline }) => (userOffline ? 'not-allowed' : 'default'),
	width: '100%',
	paddingLeft: token('space.200'),
	paddingRight: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DragToRepositionTextWrapper = styled.span({
	font: token('font.body.large'),
	color: token('color.background.input'),
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonText = styled.span({
	font: token('font.body'),
	color: token('color.text'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AcceptButtonText = styled.span({
	font: token('font.body'),
	color: token('color.text.inverse'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonGroup = styled.span({
	display: 'flex',
	gap: token('space.100'),
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RepositionImageWrapper = styled.span<{
	userOffline?: boolean;
	dynamicCoverImageHeight?: number;
}>({
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: ({ userOffline }) => (userOffline ? 'not-allowed' : 'ns-resize'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ dynamicCoverImageHeight }) => `${dynamicCoverImageHeight}px`,
	width: '100%',
	display: 'flex',
	alignItems: 'flex-start',
});

export const AIImageAcceptanceRepositioner = ({
	initialPicturePosition,
	onSaveReposition,
	onAccept,
	onDiscard,
	onRetry,
	isRetrying,
	coverPictureState,
	forceUpdate,
	userOffline,
	dynamicCoverImageHeight,
}: AIImageAcceptanceRepositionerProps) => {
	const userOfflineRef = useRef(userOffline);

	useEffect(() => {
		userOfflineRef.current = userOffline;
	}, [userOffline]);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const isLivePage = useIsCurrentPageLive();

	const handleRepositionImageMouseDown = () => {
		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
	};

	const handleMouseUp = () => {
		removeListeners();
	};

	let oldClientY = 0;
	const curPos = useRef<number>(initialPicturePosition || DEFAULT_POSITION_VALUE);

	const handleMouseMove = (e: MouseEvent) => {
		if (userOfflineRef.current) {
			removeListeners();
			return;
		}
		e.preventDefault();
		const img = document.getElementById('actual-cover-picture');
		if (e.clientY > oldClientY) {
			if (curPos.current - 0.75 >= 0) {
				curPos.current -= 0.75;
				img!.style.objectPosition = `center ${curPos.current}%`;
			}
		} else if (e.clientY < oldClientY) {
			if (curPos.current + 0.75 <= 100) {
				curPos.current += 0.75;
				img!.style.objectPosition = `center ${curPos.current}%`;
			}
		}
		oldClientY = e.clientY;
	};

	const removeListeners = () => {
		document.removeEventListener('mousemove', handleMouseMove);
		document.removeEventListener('mouseup', handleMouseUp);
	};

	const handleAccept = async () => {
		void coverPictureState.setIsRepositioningPicture(true);
		await onSaveReposition(curPos.current);
		void coverPictureState.setIsRepositioningPicture(false);
		removeListeners();
		onAccept();
		await coverPictureState.setIsChangingPicture(false);
		forceUpdate?.(Date.now());
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'aiImageAcceptanceAcceptButtonClicked',
				source: 'editorPageCoverPicture',
				attributes: {
					isLivePage,
					position: curPos.current,
				},
			},
		}).fire();
	};

	const handleDiscard = async () => {
		removeListeners();
		await coverPictureState.setIsChangingPicture(false);
		forceUpdate?.(Date.now());
		onDiscard();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'aiImageAcceptanceDiscardButtonClicked',
				source: 'editorPageCoverPicture',
				attributes: {
					isLivePage,
				},
			},
		}).fire();
	};

	const handleRetry = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'aiImageAcceptanceRetryButtonClicked',
				source: 'editorPageCoverPicture',
				attributes: {
					isLivePage,
				},
			},
		}).fire();
		onRetry();
	};

	return (
		<RepositionImageWrapper
			onMouseDown={userOffline ? undefined : handleRepositionImageMouseDown}
			data-testid="ai-image-acceptance-repositioner"
			userOffline={userOffline}
			dynamicCoverImageHeight={dynamicCoverImageHeight}
		>
			<RepositionWrapper userOffline={userOffline}>
				<DragToRepositionTextWrapper>
					{formatMessage(i18n.dragToReposition)}
				</DragToRepositionTextWrapper>
				<ButtonGroup>
					<Button
						testId="ai-image-discard-button"
						spacing="compact"
						onClick={handleDiscard}
						isDisabled={userOffline || isRetrying}
						style={{
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							background: token('color.background.input'),
						}}
					>
						<ButtonText>{formatMessage(i18n.discard)}</ButtonText>
					</Button>
					<Button
						testId="ai-image-retry-button"
						spacing="compact"
						onClick={handleRetry}
						isDisabled={userOffline || isRetrying}
						style={{
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							background: token('color.background.input'),
						}}
					>
						{isRetrying ? (
							<Spinner size="small" />
						) : (
							<ButtonText>{formatMessage(i18n.retry)}</ButtonText>
						)}
					</Button>
					<Button
						testId="ai-image-accept-button"
						spacing="compact"
						appearance="primary"
						onClick={handleAccept}
						isDisabled={userOffline || isRetrying}
					>
						<AcceptButtonText>{formatMessage(i18n.accept)}</AcceptButtonText>
					</Button>
				</ButtonGroup>
			</RepositionWrapper>
		</RepositionImageWrapper>
	);
};
